import { cn } from "@/lib/utils";
import { 
  AppTheme, 
  THEME_BACKGROUNDS, 
  THEME_ACCENTS, 
  THEME_EDGES, 
  THEME_FONTS,
  BOT_ICONS,
  BotIcon
} from "@/types/evolve";
import { Check, Flame, Bot, Brain, Zap, Skull, Dumbbell } from "lucide-react";

// Map bot icon values to Lucide components
const BOT_ICON_COMPONENTS: Record<BotIcon, React.ComponentType<{ className?: string }>> = {
  flame: Flame,
  bot: Bot,
  brain: Brain,
  zap: Zap,
  skull: Skull,
  dumbbell: Dumbbell,
};

interface ThemePickerProps {
  theme: AppTheme;
  onChange: (updates: Partial<AppTheme>) => void;
  compact?: boolean;
}

export function ThemePicker({ theme, onChange, compact = false }: ThemePickerProps) {
  // Get the actual HSL value for current background
  const currentBgHsl = THEME_BACKGROUNDS.find(b => b.value === theme.background)?.hsl || '0 0% 5%';
  
  return (
    <div className={cn("space-y-6", compact && "space-y-4")}>
      {/* Background */}
      <div>
        <h4 className={cn("font-medium mb-3", compact && "text-sm mb-2")}>Background</h4>
        <div className="grid grid-cols-2 gap-2">
          {THEME_BACKGROUNDS.map((bg) => (
            <button
              key={bg.value}
              onClick={() => onChange({ background: bg.value })}
              className={cn(
                "p-3 rounded-lg border transition-all flex items-center gap-2",
                theme.background === bg.value
                  ? "border-primary"
                  : "border-border/50 hover:border-primary/50"
              )}
              style={{ 
                backgroundColor: `hsl(${bg.hsl})`,
              }}
            >
              <div 
                className="w-6 h-6 rounded-full border-2 border-white/30"
                style={{ backgroundColor: `hsl(${bg.hsl})` }}
              />
              <span className="text-sm text-white">{bg.label}</span>
              {theme.background === bg.value && (
                <Check className="w-4 h-4 text-primary ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Accent Color */}
      <div>
        <h4 className={cn("font-medium mb-3", compact && "text-sm mb-2")}>Accent Color</h4>
        <div className="grid grid-cols-2 gap-2">
          {THEME_ACCENTS.map((accent) => (
            <button
              key={accent.value}
              onClick={() => onChange({ accent: accent.value })}
              className={cn(
                "p-3 rounded-lg border transition-all flex items-center gap-2",
                theme.accent === accent.value
                  ? "border-primary bg-primary/10"
                  : "border-border/50 hover:border-primary/50"
              )}
            >
              <div 
                className="w-6 h-6 rounded-full"
                style={{ backgroundColor: `hsl(${accent.hsl})` }}
              />
              <span className="text-sm">{accent.label}</span>
              {theme.accent === accent.value && (
                <Check className="w-4 h-4 text-primary ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Edge Style */}
      <div>
        <h4 className={cn("font-medium mb-3", compact && "text-sm mb-2")}>Edge Style</h4>
        <div className="grid grid-cols-2 gap-2">
          {THEME_EDGES.map((edge) => (
            <button
              key={edge.value}
              onClick={() => onChange({ edge: edge.value })}
              className={cn(
                "p-3 border transition-all flex items-center gap-2",
                edge.value === 'hard' ? 'rounded-sm' : 'rounded-xl',
                theme.edge === edge.value
                  ? "border-primary bg-primary/10"
                  : "border-border/50 hover:border-primary/50"
              )}
            >
              <div 
                className={cn(
                  "w-6 h-6 bg-primary/50 border border-primary",
                  edge.value === 'hard' ? 'rounded-sm' : 'rounded-lg'
                )}
              />
              <span className="text-sm">{edge.label}</span>
              {theme.edge === edge.value && (
                <Check className="w-4 h-4 text-primary ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Font */}
      <div>
        <h4 className={cn("font-medium mb-3", compact && "text-sm mb-2")}>Font Style</h4>
        <div className="grid grid-cols-2 gap-2">
          {THEME_FONTS.map((font) => (
            <button
              key={font.value}
              onClick={() => onChange({ font: font.value })}
              className={cn(
                "p-3 rounded-lg border transition-all",
                theme.font === font.value
                  ? "border-primary bg-primary/10"
                  : "border-border/50 hover:border-primary/50"
              )}
            >
              <span 
                className="text-lg font-bold block"
                style={{ fontFamily: font.display }}
              >
                Aa
              </span>
              <span className="text-xs text-muted-foreground">{font.label}</span>
              {theme.font === font.value && (
                <Check className="w-4 h-4 text-primary mx-auto mt-1" />
              )}
            </button>
        ))}
        </div>
      </div>

      {/* Bot Icon */}
      <div>
        <h4 className={cn("font-medium mb-3", compact && "text-sm mb-2")}>Coach Icon</h4>
        <div className="grid grid-cols-3 gap-2">
          {BOT_ICONS.map((icon) => {
            const IconComponent = BOT_ICON_COMPONENTS[icon.value];
            return (
              <button
                key={icon.value}
                onClick={() => onChange({ botIcon: icon.value })}
                className={cn(
                  "p-3 rounded-lg border transition-all flex flex-col items-center gap-2",
                  (theme.botIcon || 'flame') === icon.value
                    ? "border-primary bg-primary/10"
                    : "border-border/50 hover:border-primary/50"
                )}
              >
                <div className="w-10 h-10 rounded-full evolve-gradient flex items-center justify-center">
                  <IconComponent className="w-5 h-5 text-foreground" />
                </div>
                <span className="text-xs">{icon.label}</span>
                {(theme.botIcon || 'flame') === icon.value && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
